import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Card, CardContent, CardHeader } from "../../../components/ui/CCard";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Label } from "../../../components/ui/Label";
import { Textarea } from "../../../components/ui/Textarea";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from "../../../components/ui/AAccordion";
import { ArrowLeft, Save, XCircle, CheckCircle, MessageSquareText, History, Edit3, X, Plus, Quote, Trash2 } from "lucide-react";
import api from "../../../api";
import VerdictMatrix from "../../../components/VerdictMatrix";
import { useTheme } from "../../../context/ThemeContext";

export default function ConsultationReportEditor() {
  console.log("[ReportEditor] Component Mounting - Ver: 1.0.2 (Transcript Fix)");
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [consultationData, setConsultationData] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [supervisorReport, setSupervisorReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);

  // State for collapsible competencies
  const [expandedCompetencies, setExpandedCompetencies] = useState(new Set());

  // State for editable fields
  const [editableReport, setEditableReport] = useState({});
  const [editingSignal, setEditingSignal] = useState(null); // { category, key, value }
  const [modifiedSignals, setModifiedSignals] = useState({}); // { [key]: newValue }
  const [rawSignals, setRawSignals] = useState(null); // Lazy loaded
  const [transcriptMap, setTranscriptMap] = useState({}); // Mapping of turnId -> text
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [showSignals, setShowSignals] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);
  const [invalidTurns, setInvalidTurns] = useState({}); // { [sigKey]: [turnIndices] }
  const [validTurnCorrections, setValidTurnCorrections] = useState({}); // { [sigKey]: { [oldTurn]: newTurn } }

  // Enum signal definitions (Lowercase to match backend normalizer)
  const ENUM_SIGNALS = {
    "behavioral_task_specificity": ["none", "vague", "general", "specific"],
    "change_method_applied": ["none", "partial", "full"],
    "cognitive_probe_depth_level": ["none", "minimal", "moderate", "shallow", "deep"],
    "guided_discovery_depth_level": ["none", "minimal", "moderate", "shallow", "deep"],
    "intervention_specificity_depth": ["none", "minimal", "moderate", "shallow", "deep"],
    "emotional_followthrough_depth": ["none", "minimal", "moderate", "shallow", "deep"],
    "patient_airtime_ratio": ["patient-dominant", "balanced", "therapist-dominant"],
    "session_phase_balance": ["exploration-heavy", "assessment-heavy", "intervention-heavy", "balanced"],
    "empathy_statements_count": ["0", "1-2", "3-5", "5+"],
    "summarization_statements_count": ["0", "1-2", "3-5", "5+"],
    "validation_statements_count": ["0", "1-2", "3-5", "5+"],
    "open_ended_questions_count": ["0", "1-3", "4-7", "8+"],
    "closed_ended_questions_count": ["0", "1-3", "4-7", "8+"],
    "validation_depth": ["none", "minimal", "moderate", "shallow", "deep"]
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const consultationRes = await api.getConsultation(id);
        const data = consultationRes.data;
        setConsultationData(data);
        setPatientDetails(data.patients);
        setDoctorDetails(data.doctors);

        // Backend already parses supervisor_evaluation in to_dict(), so it should be a dict
        let parsedSupervisorReport = null;
        if (data.supervisor_evaluation) {
          try {
            // Backend's _parse_supervisor_evaluation() already returns a dict, but handle both cases
            parsedSupervisorReport = typeof data.supervisor_evaluation === 'string'
              ? JSON.parse(data.supervisor_evaluation)
              : data.supervisor_evaluation;

          } catch (e) {
            console.error("Failed to parse supervisor_evaluation:", e, data.supervisor_evaluation);
            setError("Failed to parse supervisor evaluation data.");
          }
        }
        setSupervisorReport(parsedSupervisorReport);

        // Initialize editable report state - Deep copy to preserve nested structures
        if (parsedSupervisorReport) {
          // Deep clone counsellor_interventions_and_skills to ensure nested objects are preserved
          const skillsCopy = parsedSupervisorReport.counsellor_interventions_and_skills
            ? JSON.parse(JSON.stringify(parsedSupervisorReport.counsellor_interventions_and_skills))
            : {};

          setEditableReport({
            executive_summary: parsedSupervisorReport.executive_summary || "",
            // Priority: 1. supervisor_evaluation.presenting_condition, 2. patient.conditions
            presenting_condition: parsedSupervisorReport.presenting_condition || parsedSupervisorReport.clinical_diagnosis?.reasoning || patientDetails?.conditions || "",
            counsellor_interventions_and_skills: skillsCopy,
            key_strengths: parsedSupervisorReport.key_strengths || [],
            action_plan: parsedSupervisorReport.action_plan || {},
            recommended_reading: parsedSupervisorReport.recommended_reading || [],
            safety_concerns: parsedSupervisorReport.safety_concerns || [],
            clinical_diagnosis: parsedSupervisorReport.clinical_diagnosis || {},
          });

          // Auto-expand first competency if available
          if (Object.keys(skillsCopy).length > 0) {
            setExpandedCompetencies(new Set([Object.keys(skillsCopy)[0]]));
          }
        } else {
          console.warn("[ReportEditor] No supervisor report found in data:", {
            hasSupervisorEval: !!data.supervisor_evaluation,
            supervisorEvalType: typeof data.supervisor_evaluation,
            dataKeys: Object.keys(data)
          });
        }

        // Load feedback history
        try {
          const feedbackRes = await api.getSessionFeedback(id);
          setFeedbackHistory(feedbackRes.data);
        } catch (err) {
          console.error("Failed to load feedback history:", err);
          // Don't fail the whole page if feedback history fails
        }

        // Load Organization General Settings
        try {
          const settingsRes = await api.getGeneralSettings();
          setOrgSettings(settingsRes.data);
        } catch (err) {
          console.error("Failed to load organization settings:", err);
        }

      } catch (err) {
        console.error("Failed to load consultation data:", err);
        setError("Failed to load consultation data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const loadRawSignalsData = async () => {
    try {
      setLoadingSignals(true);
      const res = await api.getRawSignals(id);
      const signalsData = res.data;
      setRawSignals(signalsData);
      setShowSignals(true);
      setSuccess("Clinical signals loaded successfully.");

      // Identify all unique cited turns across all signals for pre-filling
      const allTurnIds = new Set();
      const optimizedData = signalsData?.optimized || signalsData; // Fallback if already stripped

      console.log("[SignalsLoad] Extracting turns from optimized data keys:", Object.keys(optimizedData || {}));

      Object.values(optimizedData || {}).forEach(categoryObj => {
        // categoryObj is { sig1: {...}, sig2: {...} }
        Object.values(categoryObj || {}).forEach(sig => {
          // sig is { value: ..., evidence: [...], turns: [...] }
          if (sig.evidence) {
            sig.evidence.forEach(ev => {
              if (ev.turn) {
                allTurnIds.add(parseInt(ev.turn));
              }
            });
          }
          if (sig.turns) {
            sig.turns.forEach(t => {
              if (t) {
                allTurnIds.add(parseInt(t));
              }
            });
          }
        });
      });

      const turnIdsArray = Array.from(allTurnIds).filter(id => !isNaN(id));
      console.log("[SignalsLoad] Found Unique Turn IDs:", turnIdsArray);

      if (turnIdsArray.length > 0) {
        batchFetchTranscript(turnIdsArray);
      }

    } catch (err) {
      console.error("Failed to load raw signals:", err);
      setError("Failed to load clinical signals extraction data.");
    } finally {
      setLoadingSignals(false);
    }
  };

  const batchFetchTranscript = async (turnIds) => {
    if (!turnIds || turnIds.length === 0) return;
    try {
      console.log(`[TranscriptBatch] Fetching ${turnIds.length} turns:`, turnIds);
      // Backend now supports turn_ids filtering
      const response = await api.getConsultationConversation(id, { turn_ids: turnIds.join(',') });
      const conversation = response.data?.conversation || [];
      console.log(`[TranscriptBatch] Received ${conversation.length} turns from backend.`);

      const newMap = {};
      conversation.forEach(entry => {
        const tid = entry.turn_id;
        if (tid) {
          // Combine doctor and patient text if both present
          const parts = [];
          if (entry.doctor) parts.push(`Clinician: ${entry.doctor}`);
          if (entry.patient) parts.push(`Patient: ${entry.patient}`);

          const combinedText = parts.length > 0 ? parts.join("\n") : (entry.text || "");
          newMap[String(tid)] = combinedText;
        }
      });

      console.log("[TranscriptBatch] New Map keys:", Object.keys(newMap));
      setTranscriptMap(prev => ({ ...prev, ...newMap }));
    } catch (err) {
      console.error("Failed to batch fetch transcript turns:", err);
    }
  };

  // Keep open modal in sync with background transcript fetches
  useEffect(() => {
    if (editingSignal) {
      const currentQuotes = editingSignal.itemQuotes || {};
      let updated = false;
      const newQuotes = { ...currentQuotes };

      Object.keys(newQuotes).forEach(tid => {
        // If the current quote is empty or looks like a truncated placeholder (very short),
        // and we have a full transcript for it, update it.
        if ((!newQuotes[tid] || newQuotes[tid].length < 10) && transcriptMap[tid]) {
          newQuotes[tid] = transcriptMap[tid];
          updated = true;
        }
      });

      if (updated) {
        console.log("[TranscriptSync] Updating editingSignal with fetched transcript data.");
        setEditingSignal(prev => prev ? ({ ...prev, itemQuotes: newQuotes }) : null);
      }
    }
  }, [transcriptMap, editingSignal?.key]);

  // Auto-hide success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleFieldChange = (path, value) => {
    setEditableReport(prev => {
      const newReport = { ...prev };
      const pathParts = path.split('.');
      let current = newReport;
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = value;
      return newReport;
    });
  };

  // fetchInitialTranscript is replaced by batchFetchTranscript + transcriptMap

  const handleSubmitCorrection = async () => {
    if (!editingSignal) return;
    const { category, key, value, originalValue, correctionReason, itemQuotes, manualEvidenceSnippets, fullSigData } = editingSignal;

    try {
      setIsSubmitting(true);
      setSuccess(null);
      setError(null);

      // 1. Construct Original Value Object (current system state)
      const originalValueObj = {
        value: originalValue,
        cited_turns: fullSigData.turns || (fullSigData.evidence || []).map(e => e.turn)
      };

      // 2. Construct Corrected Value Object (user choices)
      const correctedValueObj = {
        value: value,
        // Merged proof: Correct system turns + Expert manual snippets
        cited_turns: originalValueObj.cited_turns.filter(t => !(invalidTurns[key] || []).includes(t)),
        manual_snippets: [
          ...manualEvidenceSnippets.filter(t => t && t.trim() !== ""),
          ...Object.entries(itemQuotes)
            .filter(([t, q]) => !(invalidTurns[key] || []).includes(parseInt(t)))
            .map(([t, q]) => q)
        ]
      };

      // 3. Add corrected turn numbers if any were provided in the Invalidation flow
      if (validTurnCorrections[key]) {
        Object.entries(validTurnCorrections[key]).forEach(([oldT, newT]) => {
          if (newT) correctedValueObj.cited_turns.push(parseInt(newT));
        });
      }

      const payload = {
        session_id: parseInt(id),
        signal_id: key,
        original_value: originalValueObj,
        corrected_value: correctedValueObj,
        correction_type: typeof value === 'object' ? (value.value !== originalValue ? 'score' : 'turn') : 'signal',
        transcript_excerpt: correctedValueObj.manual_snippets.join("\n---\n"),
        correction_reason: correctionReason || "Expert manual correction",
        category: category
      };

      await api.submitFeedback(payload);
      setModifiedSignals(prev => ({ ...prev, [key]: value }));
      setEditingSignal(null);
      setSuccess(`Correction for "${editingSignal.name}" applied successfully.`);

      // Refresh feedback history
      try {
        const feedbackRes = await api.getSessionFeedback(id);
        const sortedHistory = (feedbackRes.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setFeedbackHistory(sortedHistory);
      } catch (err) {
        console.error("Failed to refresh feedback history:", err);
      }
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm("Are you sure you want to delete this correction? This will remove it from the training data.")) return;

    try {
      await api.deleteFeedback(feedbackId);
      setSuccess("Feedback deleted successfully.");
      // Refresh history
      const res = await api.getSessionFeedback(id);
      setFeedbackHistory((res.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      console.error("Failed to delete feedback:", err);
      setError("Failed to delete feedback. Please try again.");
    }
  };

  const renderEditableField = (label, path, originalValue, isTextarea = true, category = "general") => {
    // Handle nested paths like "agenda_setting.specific_observations"
    // These need to be accessed via counsellor_interventions_and_skills
    let currentValue;
    if (path.includes('.') && !path.startsWith('counsellor_interventions_and_skills')) {
      // This is a competency field path like "agenda_setting.specific_observations"
      const [compKey, fieldKey] = path.split('.');
      currentValue = editableReport.counsellor_interventions_and_skills?.[compKey]?.[fieldKey] || "";
    } else {
      // This is a top-level field like "executive_summary"
      currentValue = path.split('.').reduce((acc, part) => acc && acc[part], editableReport) || "";
    }
    const fieldName = path; // For simple fields, fieldName is the path
    const hasChanges = currentValue !== originalValue && currentValue !== (originalValue || "");

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-secondary-700 dark:text-secondary-300 font-semibold">
            {label}
          </Label>
          {hasChanges && (
            <span className="text-xs text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded">
              Modified
            </span>
          )}
        </div>
        {isTextarea ? (
          <Textarea
            value={currentValue || ""}
            onChange={(e) => handleFieldChange(path, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}...`}
            className="bg-white dark:bg-secondary-900 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 rounded-lg text-sm leading-relaxed min-h-[120px] resize-y"
          />
        ) : (
          <Input
            type="text"
            value={currentValue || ""}
            onChange={(e) => handleFieldChange(path, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}...`}
            className="bg-white dark:bg-secondary-900 border-secondary-300 dark:border-secondary-600 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 rounded-lg text-sm"
          />
        )}
        {originalValue && (
          <p className="text-xs text-secondary-500 dark:text-secondary-400 italic mt-1">
            Original: {originalValue.substring(0, 100)}{originalValue.length > 100 ? '...' : ''}
          </p>
        )}
      </div>
    );
  };

  const toggleCompetency = (compKey) => {
    setExpandedCompetencies(prev => {
      // If clicking on an already expanded section, collapse it
      if (prev.has(compKey)) {
        return new Set(); // Collapse all
      } else {
        // If clicking on a collapsed section, expand only this one (close others)
        return new Set([compKey]);
      }
    });
  };

  const hasCompetencyChanges = (compKey, originalCompData) => {
    if (!originalCompData) return false;
    const currentComp = editableReport.counsellor_interventions_and_skills?.[compKey];
    if (!currentComp) return false;

    const fields = ['specific_observations', 'positive_feedback', 'negative_feedback', 'suggested_phrasing', 'curated_action_step'];
    return fields.some(field => {
      const current = currentComp[field] || "";
      const original = originalCompData[field] || "";
      return current !== original;
    });
  };

  const handleSubmitCompetencyCorrection = async (compKey, originalCompData) => {
    const currentComp = editableReport.counsellor_interventions_and_skills?.[compKey];
    if (!currentComp) return;

    // Submit each changed field
    const fields = [
      { key: 'specific_observations', label: 'Specific Observations' },
      { key: 'positive_feedback', label: 'Positive Feedback' },
      { key: 'negative_feedback', label: 'Negative Feedback' },
      { key: 'suggested_phrasing', label: 'Suggested Phrasing' },
      { key: 'curated_action_step', label: 'Curated Action Step' }
    ];

    let submittedCount = 0;
    for (const field of fields) {
      const currentValue = currentComp[field.key] || "";
      const originalValue = originalCompData?.[field.key] || "";

      if (currentValue !== originalValue) {
        const fieldName = `${compKey}.${field.key}`;
        try {
          await handleSubmitCorrection("competency_feedback", fieldName, originalValue, currentValue, compKey);
          submittedCount++;
        } catch (err) {
          console.error(`Failed to submit ${field.label}:`, err);
        }
      }
    }

    if (submittedCount > 0) {
      setSuccess(`Successfully submitted ${submittedCount} correction(s) for this competency.`);
    } else {
      setError("No changes detected for this competency.");
    }
  };

  const renderCompetencyField = (compKey, compData, originalCompData) => {
    if (!compData || typeof compData !== 'object') {
      console.warn(`[ReportEditor] Invalid compData for ${compKey}:`, compData);
      return null;
    }

    const isExpanded = expandedCompetencies.has(compKey);
    const hasChanges = hasCompetencyChanges(compKey, originalCompData);
    const competencyName = compData.name || compKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <div key={compKey} className="border border-secondary-200 dark:border-secondary-700 rounded-xl mb-4 overflow-hidden bg-white dark:bg-secondary-800 shadow-sm">
        {/* Header - Always Visible */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors"
          onClick={() => toggleCompetency(compKey)}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
              <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                {competencyName}
              </h4>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                Score: <span className="font-bold text-primary-600 dark:text-primary-400">{compData.score ?? 'N/A'}</span> / {supervisorReport?.rubric_info?.scale_max ?? 6}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-xs text-primary-600 dark:text-primary-400 font-medium bg-primary-50 dark:bg-primary-900/20 px-3 py-1 rounded-full">
                Modified
              </span>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSubmitCompetencyCorrection(compKey, originalCompData);
              }}
              disabled={!hasChanges}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium shadow-sm ${hasChanges
                ? "bg-primary-500 hover:bg-primary-600 text-white"
                : "bg-secondary-200 dark:bg-secondary-700 text-secondary-400 dark:text-secondary-500 cursor-not-allowed"
                }`}
            >
              <Edit3 className="w-4 h-4" /> Submit All Changes
            </button>
          </div>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <div className="border-t border-secondary-200 dark:border-secondary-700 p-6 bg-secondary-50/50 dark:bg-secondary-900/50">
            <div className="space-y-4">
              {renderEditableField("Specific Observations", `${compKey}.specific_observations`, originalCompData?.specific_observations || "", true, "competency_feedback")}
              {renderEditableField("Positive Feedback", `${compKey}.positive_feedback`, originalCompData?.positive_feedback || "", true, "competency_feedback")}
              {renderEditableField("Negative Feedback / Areas for Improvement", `${compKey}.negative_feedback`, originalCompData?.negative_feedback || "", true, "competency_feedback")}
              {renderEditableField("Suggested Phrasing", `${compKey}.suggested_phrasing`, originalCompData?.suggested_phrasing || "", true, "competency_feedback")}
              {renderEditableField("Curated Action Step", `${compKey}.curated_action_step`, originalCompData?.curated_action_step || "", true, "competency_feedback")}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto p-6 text-center text-secondary-500">Loading report...</div>
      </DashboardLayout>
    );
  }

  if (error && !supervisorReport) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto p-6 text-center text-red-500">Error: {error}</div>
      </DashboardLayout>
    );
  }

  if (!consultationData || !supervisorReport) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto p-6">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center">
            <p className="text-yellow-800 dark:text-yellow-200 font-medium mb-2">No supervisor report found for this consultation.</p>
            <p className="text-sm text-yellow-600 dark:text-yellow-400">
              Please generate an evaluation first by going back to the consultation page and clicking "Generate Summary".
            </p>
            <button
              onClick={() => navigate(`/consultation/${id}`)}
              className="mt-4 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Back to Consultation
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const institutionalBenchmark = supervisorReport.institutional_benchmark;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 dark:text-secondary-100">
              Expert Report Editor
            </h1>
            <p className="text-secondary-600 dark:text-secondary-400 mt-1">
              Consultation #{id} - {patientDetails?.name || "Unknown Patient"}
            </p>
          </div>
          <button
            onClick={() => navigate(`/consultation/${id}`)}
            className="bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 dark:bg-secondary-800 dark:border-secondary-700 dark:text-primary-300 dark:hover:bg-secondary-700 px-4 py-2 rounded-xl flex items-center gap-2 shadow-sm hover:shadow-md transition-all font-semibold text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Consultation
          </button>
        </div>

        {/* Fixed Position Toast Messages - Always Visible */}
        {success && (
          <div className="fixed top-4 right-4 z-[9999] p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl shadow-lg max-w-md animate-in slide-in-from-top-5">
            <p className="text-primary-800 dark:text-primary-200 font-medium">{success}</p>
          </div>
        )}
        {error && (
          <div className="fixed top-4 right-4 z-[9999] p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl shadow-lg max-w-md animate-in slide-in-from-top-5">
            <p className="text-red-800 dark:text-red-200 font-medium">{error}</p>
          </div>
        )}

        {/* Feedback History Toggle */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowFeedbackHistory(!showFeedbackHistory)}
            className={`bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 dark:bg-secondary-800 dark:border-secondary-700 dark:text-primary-300 dark:hover:bg-secondary-700 px-4 py-2 rounded-xl flex items-center gap-2 shadow-sm hover:shadow-md transition-all font-semibold text-sm ${showFeedbackHistory ? 'bg-primary-100 dark:bg-primary-900' : ''}`}
          >
            <History className="w-4 h-4" />
            {showFeedbackHistory ? "Hide Feedback History" : "Show Feedback History"} ({feedbackHistory.length})
          </button>
        </div>

        {/* Feedback History Section */}
        {showFeedbackHistory && (
          <Card className="bg-white dark:bg-secondary-800 shadow-md rounded-xl">
            <CardHeader className="flex items-center justify-between bg-secondary-50 dark:bg-secondary-700 border-b border-secondary-200 dark:border-secondary-600 p-3">
              <h2 className="text-base font-semibold text-secondary-900 dark:text-secondary-100">Feedback History</h2>
            </CardHeader>
            <CardContent className="p-3">
              {feedbackHistory.length === 0 ? (
                <p className="text-xs text-secondary-600 dark:text-secondary-400">No history.</p>
              ) : (
                <div className="space-y-2">
                  {feedbackHistory.map((feedback) => {
                    // Helper to render structured feedback data (either from object or parsed string)
                    const renderStructuredFeedback = (data) => {
                      if (!data || typeof data !== 'object') return null;
                      return (
                        <div className="space-y-1 mt-1">
                          <div className="flex items-center gap-2">
                            <span className="px-1 py-0.5 bg-primary-50 dark:bg-primary-900/40 text-primary-600 rounded text-[9px] font-bold">
                              VALUE: {String(data.value).toUpperCase()}
                            </span>
                            {data.cited_turns && data.cited_turns.length > 0 && (
                              <span className="px-1 py-0.5 bg-secondary-100 dark:bg-secondary-700 text-secondary-600 rounded text-[9px]">
                                TURNS: {data.cited_turns.join(', ')}
                              </span>
                            )}
                          </div>
                          {data.manual_snippets && data.manual_snippets.length > 0 && (
                            <div className="pl-2 border-l-2 border-primary-200 mt-1">
                              {data.manual_snippets.map((s, idx) => (
                                <p key={idx} className="text-[10px] text-secondary-500 italic line-clamp-2">"{s}"</p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    };

                    let formattedCorrection = feedback.corrected_text;

                    // CASE 1: Structured Object (New Backend Format)
                    if (feedback.corrected_text && typeof feedback.corrected_text === 'object') {
                      formattedCorrection = renderStructuredFeedback(feedback.corrected_text);
                    }
                    // CASE 2: Python-style String (Legacy Production Data)
                    else if (typeof feedback.corrected_text === 'string' && (feedback.corrected_text.startsWith('{') || feedback.corrected_text.startsWith('['))) {
                      try {
                        const jsonCompatible = feedback.corrected_text
                          .replace(/'/g, '"')
                          .replace(/True/g, 'true')
                          .replace(/False/g, 'false')
                          .replace(/None/g, 'null');
                        const data = JSON.parse(jsonCompatible);
                        if (data && typeof data === 'object') {
                          formattedCorrection = renderStructuredFeedback(data);
                        }
                      } catch (e) {
                        console.warn("[ReportEditor] Failed to parse legacy feedback string:", feedback.corrected_text, e);
                        formattedCorrection = feedback.corrected_text;
                      }
                    }

                    return (
                      <div key={feedback.id} className="group relative p-3 rounded-xl hover:bg-secondary-50 dark:hover:bg-secondary-900/40 transition-all border border-transparent hover:border-secondary-100 dark:hover:border-secondary-700">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[9px] font-bold uppercase tracking-wider text-primary-500">
                                {feedback.category}
                              </span>
                              <span className="text-secondary-300">•</span>
                              <span className="text-[10px] font-bold text-secondary-700 dark:text-secondary-200">
                                {feedback.field_name?.replace(/_/g, ' ')}
                              </span>
                              {feedback.expert_name && (
                                <>
                                  <span className="text-secondary-300">•</span>
                                  <span className="text-[9px] text-secondary-500 font-medium italic">
                                    by {feedback.expert_name}
                                  </span>
                                </>
                              )}
                            </div>

                            <div className="text-xs text-secondary-600 dark:text-secondary-400">
                              {typeof formattedCorrection === 'string' ? (
                                <p className="line-clamp-3">{formattedCorrection}</p>
                              ) : (
                                formattedCorrection
                              )}
                            </div>

                            {feedback.reason && (
                              <div className="mt-2 text-[10px] text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20 p-1.5 rounded-lg flex gap-2 items-start">
                                <MessageSquareText className="w-3 h-3 mt-0.5 shrink-0" />
                                <span className="italic">Expert Reason: {feedback.reason}</span>
                              </div>
                            )}

                            <div className="mt-2 text-[9px] text-secondary-400 font-medium">
                              {new Date(feedback.created_at).toLocaleString()}
                            </div>
                          </div>

                          <button
                            onClick={() => handleDeleteFeedback(feedback.id)}
                            className="p-1.5 text-secondary-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                            title="Delete this correction"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Departmental Benchmarks */}
        <Card className="bg-white dark:bg-secondary-800 shadow-lg rounded-2xl border-none">
          <CardHeader className="flex items-center justify-between bg-secondary-50 dark:bg-secondary-700/30 border-b border-secondary-100 dark:border-secondary-700 p-3">
            <h2 className="text-base font-bold text-secondary-900 dark:text-secondary-100 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary-500" />
              Departmental Benchmarks
            </h2>
          </CardHeader>
          <CardContent className="p-3">
            <VerdictMatrix
              scoringBreakdown={supervisorReport?.scoring_breakdown}
              orgSettings={orgSettings}
            />
          </CardContent>
        </Card>

        {/* Clinical Signals Section - Lazy Loaded */}
        <Card className="bg-white dark:bg-secondary-800 shadow-xl rounded-2xl border-none overflow-hidden">
          <CardHeader className="flex flex-col md:flex-row md:items-center justify-between bg-gradient-to-r from-secondary-50 to-white dark:from-secondary-900/50 dark:to-secondary-800 p-4 border-b border-secondary-100 dark:border-secondary-700 gap-3">
            <div className="space-y-0.5">
              <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 tracking-tight flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center text-white shadow-md shadow-primary-500/10">
                  <MessageSquareText className="w-5 h-5" />
                </div>
                Clinical Signals Review
              </h2>
              <p className="text-secondary-500 dark:text-secondary-400 text-[11px] font-medium">
                Validate and correct clinical signals extracted from the transcript.
              </p>
            </div>

            {!showSignals ? (
              <Button
                onClick={loadRawSignalsData}
                disabled={loadingSignals}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 h-9 rounded-lg font-bold text-sm shadow-md shadow-primary-500/10 transition-all transform hover:-translate-y-0.5 group"
              >
                {loadingSignals ? (
                  <span className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Extracting Signals...
                  </span>
                ) : (
                  <span className="flex items-center gap-3">
                    <MessageSquareText className="w-4 h-4 group-hover:scale-105 transition-transform" />
                    Review Clinical Signals
                  </span>
                )}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowSignals(false)}
                className="h-8 rounded-lg font-bold border-secondary-200 dark:border-secondary-700 text-xs px-3"
              >
                Hide Signals
              </Button>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {!showSignals ? (
              <div className="p-6 text-center space-y-2">
                <div className="w-12 h-12 bg-secondary-50 dark:bg-secondary-900 rounded-full flex items-center justify-center mx-auto mb-3 text-secondary-300">
                  <Edit3 className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-secondary-800 dark:text-secondary-200">Signals are ready</h3>
                <p className="text-[11px] text-secondary-500 max-w-xs mx-auto">
                  Click the button above to load the extracted signals.
                </p>
              </div>
            ) : (
              <div className="p-6 md:p-8 animate-in fade-in slide-in-from-bottom-5 duration-500">
                {(() => {
                  const data = rawSignals?.optimized || {};
                  const therapyType = (consultationData?.therapy_type || "CBT").toLowerCase();

                  const categories = [
                    { id: "safety", label: "Safety Critical", data: data.safety || {}, color: "red", icon: "🛡️" },
                    { id: "communication", label: "Communication Flow", data: data.communication || {}, color: "blue", icon: "💬" },
                    { id: therapyType, label: `${therapyType.toUpperCase()} Core`, data: data[therapyType] || {}, color: "amber", icon: "🎯" }
                  ];

                  return (
                    <div className="space-y-12">
                      {categories.map(cat => (
                        <div key={cat.id} className="space-y-6">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{cat.icon}</span>
                            <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 tracking-tight">{cat.label}</h3>
                            <div className="flex-1 h-px bg-gradient-to-r from-secondary-200 to-transparent dark:from-secondary-700" />
                          </div>

                          {Object.keys(cat.data).length === 0 ? (
                            <div className="bg-secondary-50 dark:bg-secondary-900/40 rounded-xl p-4 text-center border-2 border-dashed border-secondary-200 dark:border-secondary-800">
                              <p className="text-[11px] text-secondary-400 font-medium italic">No signals detected.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                              {Object.entries(cat.data).map(([sigKey, sigData]) => {
                                const name = sigKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                const value = sigData.value;
                                const isModified = modifiedSignals[sigKey] !== undefined;

                                let displayValue = "";
                                let valColor = "text-secondary-400";
                                let valBg = "bg-secondary-100 dark:bg-secondary-800";

                                if (typeof value === 'boolean') {
                                  displayValue = value ? "PRESENT" : "ABSENT";
                                  valColor = value ? "text-green-600 dark:text-green-400" : "text-secondary-400";
                                  valBg = value ? "bg-green-50 dark:bg-green-900/20" : "bg-secondary-50 dark:bg-secondary-900/40";
                                } else {
                                  displayValue = String(value || "0").toUpperCase();
                                  valColor = "text-primary-600 dark:text-primary-400";
                                  valBg = "bg-primary-50 dark:bg-primary-900/20";
                                }

                                return (
                                  <div key={sigKey} className={`relative group rounded-xl border transition-all duration-300 flex flex-col border-secondary-100 dark:border-secondary-800 hover:border-primary-200 dark:hover:border-primary-800 hover:shadow-md bg-white dark:bg-secondary-800/50`}>
                                    <div className="p-6 flex-1">
                                      <div className="flex justify-between items-start mb-4">
                                        <div className="space-y-1">
                                          <h4 className="font-bold text-secondary-900 dark:text-secondary-100 leading-tight pr-8">
                                            {name}
                                          </h4>
                                        </div>
                                        <button
                                          onClick={() => {
                                            setEditingSignal({
                                              category: cat.id,
                                              key: sigKey,
                                              name: name,
                                              value: isModified ? modifiedSignals[sigKey] : value,
                                              originalValue: value,
                                              supportingSnippet: "",
                                              manualEvidenceSnippets: [],
                                              itemQuotes: (() => {
                                                const quotes = (sigData.evidence || []).reduce((acc, ev) => {
                                                  const tid = String(ev.turn);
                                                  // Prefer freshly fetched transcript text if available
                                                  acc[tid] = transcriptMap[tid] || ev.quote || "";
                                                  return acc;
                                                }, {});
                                                (sigData.turns || []).forEach(t => {
                                                  const tid = String(t);
                                                  if (!quotes[tid]) quotes[tid] = transcriptMap[tid] || "";
                                                });
                                                return quotes;
                                              })(),
                                              fullSigData: sigData,
                                              correctionReason: ""
                                            });
                                          }}
                                          className={`p-2 bg-secondary-50 dark:bg-secondary-900 text-secondary-400 hover:text-primary-500 rounded-xl transition-all`}
                                        >
                                          <Edit3 className="w-4 h-4" />
                                        </button>
                                      </div>

                                      <div className="space-y-4">
                                        <div className={`px-2 py-1 rounded-lg w-fit font-bold text-[10px] tracking-wider ${valBg} ${valColor}`}>
                                          {isModified
                                            ? (typeof modifiedSignals[sigKey] === 'boolean' ? (modifiedSignals[sigKey] ? 'PRESENT' : 'ABSENT') : modifiedSignals[sigKey])
                                            : displayValue}
                                        </div>
                                        {sigData.logic_note && (
                                          <p className="text-xs text-secondary-500 dark:text-secondary-400 italic line-clamp-2 leading-relaxed">
                                            "{sigData.logic_note}"
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {(sigData.evidence?.length > 0 || sigData.turns?.length > 0) && (
                                      <div className="border-t border-secondary-50 dark:border-secondary-800/50 p-3">
                                        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-secondary-400">
                                          <Quote className="w-3 h-3" />
                                          {sigData.evidence?.length || sigData.turns?.length} System Citations
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signal Edit Modal */}
        {editingSignal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col border border-secondary-100 dark:border-secondary-700 animate-in zoom-in-95 duration-300">
              <div className="p-5 border-b border-secondary-100 dark:border-secondary-700 bg-secondary-50/30 dark:bg-secondary-900/40">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 rounded text-[9px] font-bold uppercase tracking-wider">
                        {editingSignal.category}
                      </span>
                      <h3 className="text-sm font-bold text-secondary-700 dark:text-secondary-200 truncate pr-4">
                        {editingSignal.name}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditingSignal(null)}
                    className="p-1.5 text-secondary-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {editingSignal.fullSigData.logic_note && (
                  <div className="mt-3 p-3 bg-blue-50/40 border border-blue-100/50 rounded-xl">
                    <p className="text-[10.5px] text-blue-700 leading-relaxed">
                      <span className="font-bold uppercase mr-1.5 opacity-70">System Logic:</span>
                      {editingSignal.fullSigData.logic_note}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {/* 1. Value Switcher */}
                <div className="space-y-3">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-secondary-600 dark:text-secondary-400">
                    Is the System Score Correct?
                  </Label>
                  {ENUM_SIGNALS[editingSignal.key] ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {ENUM_SIGNALS[editingSignal.key].map(opt => (
                        <button
                          key={opt}
                          onClick={() => setEditingSignal(p => ({ ...p, value: opt }))}
                          className={`py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all duration-200 border ${editingSignal.value === opt ? 'bg-primary-500 border-primary-500 text-white shadow-sm' : 'bg-white dark:bg-secondary-900 border-secondary-100 dark:border-secondary-700 text-secondary-400 hover:border-primary-200'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : typeof editingSignal.originalValue === 'boolean' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setEditingSignal(p => ({ ...p, value: true }))}
                        className={`py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all duration-300 border flex items-center justify-center gap-2 ${editingSignal.value === true ? 'bg-green-500 border-green-500 text-white shadow-sm' : 'bg-white dark:bg-secondary-900 border-secondary-100 dark:border-secondary-700 text-secondary-400 hover:border-green-200'}`}
                      >
                        <CheckCircle className={`w-3.5 h-3.5 ${editingSignal.value === true ? 'animate-bounce' : ''}`} />
                        PRESENT
                      </button>
                      <button
                        onClick={() => setEditingSignal(p => ({ ...p, value: false }))}
                        className={`py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all duration-300 border flex items-center justify-center gap-2 ${editingSignal.value === false ? 'bg-red-500 border-red-500 text-white shadow-sm' : 'bg-white dark:bg-secondary-900 border-secondary-100 dark:border-secondary-700 text-secondary-400 hover:border-red-200'}`}
                      >
                        <XCircle className={`w-3.5 h-3.5 ${editingSignal.value === false ? 'animate-shake' : ''}`} />
                        ABSENT
                      </button>
                    </div>
                  ) : (
                    <Input
                      value={editingSignal.value}
                      onChange={(e) => setEditingSignal(p => ({ ...p, value: e.target.value }))}
                      className="bg-secondary-50 dark:bg-secondary-900 border-secondary-200 dark:border-secondary-700 rounded-lg h-8 font-bold text-base text-center"
                    />
                  )}
                </div>

                {/* 2. Proof-Reading Section (Conditional) */}
                {(editingSignal.fullSigData.evidence || []).length > 0 && (
                  <div className="space-y-3 pt-2">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-secondary-600 dark:text-secondary-400">
                      Proof-Read System Observations
                    </Label>
                    <div className="space-y-2">
                      {(editingSignal.fullSigData.evidence || []).map((ev, i) => {
                        const isRejected = (invalidTurns[editingSignal.key] || []).includes(ev.turn);
                        return (
                          <div key={i} className={`p-3 rounded-lg border transition-all ${isRejected ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 opacity-60' : 'bg-secondary-50 dark:bg-secondary-900/30 border-secondary-100'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[9px] font-bold uppercase text-secondary-400">Turn {ev.turn}</span>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => setInvalidTurns(prev => ({
                                    ...prev,
                                    [editingSignal.key]: (prev[editingSignal.key] || []).filter(t => t !== ev.turn)
                                  }))}
                                  className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase transition-all ${!isRejected ? 'bg-green-500 text-white' : 'bg-secondary-100 text-secondary-400'}`}
                                >
                                  Correct
                                </button>
                                <button
                                  onClick={() => setInvalidTurns(prev => ({
                                    ...prev,
                                    [editingSignal.key]: [...(prev[editingSignal.key] || []), ev.turn]
                                  }))}
                                  className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase transition-all ${isRejected ? 'bg-red-500 text-white' : 'bg-red-50 text-red-500'}`}
                                >
                                  Incorrect
                                </button>
                              </div>
                            </div>
                            <Textarea
                              className="w-full min-h-[44px] max-h-[100px] bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 rounded-lg text-[11px] p-2 focus:ring-1 focus:ring-primary-400 text-secondary-700 leading-tight italic"
                              value={editingSignal.itemQuotes[String(ev.turn)] || ev.quote || ""}
                              onChange={(e) => {
                                const newText = e.target.value;
                                setEditingSignal(p => ({
                                  ...p,
                                  itemQuotes: { ...p.itemQuotes, [String(ev.turn)]: newText }
                                }));
                              }}
                              placeholder="System observation text..."
                            />
                            {isRejected && (
                              <div className="mt-2 animate-in slide-in-from-top-1">
                                <label className="text-[8px] font-bold text-red-500 uppercase">Correct Turn Number (Optional):</label>
                                <Input
                                  type="number"
                                  className="h-7 text-[10px] w-20 mt-1"
                                  placeholder="#"
                                  value={validTurnCorrections[editingSignal.key]?.[ev.turn] || ""}
                                  onChange={(e) => setValidTurnCorrections(prev => ({
                                    ...prev,
                                    [editingSignal.key]: { ...(prev[editingSignal.key] || {}), [ev.turn]: e.target.value }
                                  }))}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 3. Expert Contribution (Optional) */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-secondary-600 dark:text-secondary-400">
                      Add Proof System Missed (Optional)
                    </Label>
                    <button
                      className="text-[9px] font-bold text-primary-500 uppercase hover:underline"
                      onClick={() => setEditingSignal(p => ({ ...p, manualEvidenceSnippets: ["", ...p.manualEvidenceSnippets] }))}
                    >
                      + Add Expert Quote
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editingSignal.manualEvidenceSnippets.map((snippet, idx) => (
                      <div key={idx} className="relative group p-3 bg-primary-50/30 border border-primary-100 rounded-xl">
                        <Textarea
                          className="min-h-[44px] max-h-[100px] bg-transparent border-none text-[11px] p-0 focus:ring-0 text-secondary-700 leading-tight"
                          placeholder="Paste a specific quote the system missed..."
                          value={snippet}
                          onChange={(e) => {
                            const newSnippets = [...editingSignal.manualEvidenceSnippets];
                            newSnippets[idx] = e.target.value;
                            setEditingSignal(p => ({ ...p, manualEvidenceSnippets: newSnippets }));
                          }}
                        />
                        <button
                          onClick={() => setEditingSignal(p => ({ ...p, manualEvidenceSnippets: p.manualEvidenceSnippets.filter((_, i) => i !== idx) }))}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                    {editingSignal.manualEvidenceSnippets.length === 0 && (
                      <p className="text-[10px] text-secondary-400 italic text-center py-2 border border-dashed border-secondary-100 rounded-lg">
                        Only use this if you want to add proof the system didn't catch.
                      </p>
                    )}
                  </div>
                </div>

                {/* 4. Clinical Reasoning (Explicit for persisted reason) */}
                <div className="space-y-3 pt-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-secondary-600 dark:text-secondary-400">
                    Your Clinical Reasoning (Persisted for Training)
                  </Label>
                  <div className="p-3 bg-white dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-inner-sm">
                    <Textarea
                      className="min-h-[60px] max-h-[120px] bg-transparent border-none text-[11px] p-0 focus:ring-0 text-secondary-700 leading-relaxed"
                      placeholder="Why is your correction more accurate than the system? (e.g., 'System missed nuance in turn 5...')"
                      value={editingSignal.correctionReason}
                      onChange={(e) => setEditingSignal(p => ({ ...p, correctionReason: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-secondary-100 dark:border-secondary-700 bg-secondary-50/50 dark:bg-secondary-900/20 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingSignal(null)}
                  className="flex-1 h-8 rounded-lg font-bold text-secondary-600 border-secondary-200 dark:border-secondary-700 dark:text-secondary-400 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-[2] h-9 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-bold shadow-md shadow-primary-500/10 transform hover:-translate-y-0.5 transition-all text-xs"
                  onClick={handleSubmitCorrection}
                >
                  Save & Apply Correction
                </Button>
              </div>
            </div>
          </div>
        )}

      </div >
    </DashboardLayout >
  );
}
